import { docxTemplateRequest, xlsxTemplateRequest } from "../api/docTemplates";
import { directory, lookupSender, nameKey, namedSigner, suggestPeople, type DirectoryPerson } from "../api/epflDirectory.svelte";
import { extractFileBlocks, generatedRevision, isFileRevision, type FileBlock } from "../api/fileBlocks";
import { chatRequest, reasoningParams } from "../api/rcpClient";
import { answerModel, type Config } from "./config";
import { tr } from "../i18n/i18n.svelte";
import { conversationFileBlocks } from "./originals";

// Signataire introuvable dans l'annuaire, à la fin de la réponse (03.11.2026).
//
// « Signe de la main d'Anna Voncuberta » : l'annuaire EPFL ne cherche que par
// début de mot et ne tolère aucune faute, et le modèle ne peut pas le
// consulter. Avant d'enregistrer la réponse, on cherche donc nous-mêmes des
// personnes au nom proche (chaque mot, ses premières lettres, classées par
// ressemblance), puis on les soumet au modèle en un court tour : si l'une est
// clairement la personne voulue, le document passe à son nom exact ; sinon la
// réponse se termine par une question à l'usager, candidats à l'appui.

const CHECK_TIMEOUT_MS = 30_000;

const CHECK_SYSTEM = `Tu aides à préparer un document EPFL écrit au nom d'une personne. Le nom que tu as utilisé ne figure pas tel quel dans l'annuaire EPFL, qui ne cherche que par début de mot et ne tolère aucune faute ; l'interface y a cherché les personnes au nom le plus proche. Merci de choisir avec soin : un document signé au nom de la mauvaise personne serait gênant pour l'usager.

On te donne la demande de l'usager, le nom utilisé, les recherches déjà faites et les candidats trouvés (nom, fonction, unité).
- Si un candidat est sans doute possible la personne voulue (faute de frappe, nom incomplet, accent, nom composé abrégé, personnalité connue de l'EPFL) : {"choix": <numéro du candidat>}.
- Si aucun candidat ne convient mais que tu penses à une autre orthographe plausible (nom entendu ou mal transcrit, personnalité que tu connais, prénom ou nom de famille complet) : {"chercher": ["Prénom Nom", …]}, trois orthographes au plus, différentes de celles déjà cherchées. L'interface les cherchera dans l'annuaire et te rendra les candidats. Possible seulement quand on te l'indique.
- Sinon (plusieurs candidats plausibles, ou aucun) : {"question": "<une question courte à l'usager, dans sa langue, qui propose les candidats plausibles avec leur fonction, ou lui demande le nom exact tel qu'il figure dans l'annuaire>"}.

Réponds UNIQUEMENT par cet objet JSON, sans texte autour. Merci !`;

/** Recherches que le modèle peut demander en plus de la première. */
const EXTRA_ROUNDS = 2;

/** Journal des appels à l'annuaire : une ligne par appel, complétée par son
 *  résultat (montrée sous « Comment la réponse a été construite »). */
export type DirectoryLog = (query: string) => (result: string, failed?: boolean) => void;

export interface SignerReport {
  /** Le message : bloc corrigé et note, ou question ajoutée ; inchangé sinon. */
  content: string;
  /** Résumé pour le journal d'activité, null si rien n'a été cherché. */
  summary: string | null;
}

/** Le texte complet du fichier, révision d'un fichier généré comprise. */
function effectiveText(block: FileBlock): string {
  if (!isFileRevision(block)) return block.body;
  return generatedRevision(block, conversationFileBlocks())?.text ?? "";
}

/** La personne au nom de qui un fichier au modèle EPFL est écrit, si ce n'est pas l'usager. */
function signerOf(block: FileBlock): string {
  const text = effectiveText(block);
  const req = block.ext === "docx" ? docxTemplateRequest(text) : block.ext === "xlsx" ? xlsxTemplateRequest(text) : null;
  return req ? namedSigner(req.fields, directory.user) : "";
}

/** Le message contient-il un fichier au nom d'une autre personne ? */
export function hasNamedSigner(content: string): boolean {
  return extractFileBlocks(content).some((b) => b.complete && !!signerOf(b));
}

function describe(p: DirectoryPerson): string {
  const a = p.accreds[0];
  return [p.name, a?.position, a ? `${a.unitName} (${a.acronym})` : ""].filter(Boolean).join(" — ");
}

type Verdict = { choix: number } | { chercher: string[] } | { question: string };

export function parseVerdict(raw: string, n: number): Verdict | null {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    const data = JSON.parse(raw.slice(start, end + 1)) as Record<string, unknown>;
    const choice = Number(data.choix);
    if (Number.isInteger(choice) && choice >= 1 && choice <= n) return { choix: choice };
    if (Array.isArray(data.chercher)) {
      const names = data.chercher.filter((x): x is string => typeof x === "string" && x.trim().length > 0).map((x) => x.trim()).slice(0, 3);
      if (names.length > 0) return { chercher: names };
    }
    if (typeof data.question === "string" && data.question.trim()) return { question: data.question.trim() };
  } catch {
    /* réponse illisible */
  }
  return null;
}

async function askModel(
  question: string,
  name: string,
  tried: string[],
  candidates: DirectoryPerson[],
  canSearch: boolean,
  cfg: Config,
  signal?: AbortSignal,
): Promise<Verdict | null> {
  const list = candidates.length > 0 ? candidates.map((p, i) => `${i + 1}. ${describe(p)}`).join("\n") : "aucun";
  const prompt = [
    `Demande de l'usager : ${question}`,
    `Nom utilisé : ${name}`,
    `Déjà cherché dans l'annuaire : ${tried.map((t) => `« ${t} »`).join(", ")}`,
    `Candidats de l'annuaire EPFL :\n${list}`,
    canSearch ? "Tu peux demander une autre recherche (« chercher »)." : "Plus de recherche possible : choisis ou pose la question.",
  ].join("\n\n");
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
  const onAbort = () => controller.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const model = answerModel(cfg);
    const data = await chatRequest(
      {
        model,
        max_tokens: 1500,
        ...reasoningParams(model, "bas"),
        temperature: 0,
        messages: [
          { role: "system", content: CHECK_SYSTEM },
          { role: "user", content: prompt },
        ],
      },
      cfg,
      controller.signal,
    );
    const verdict = parseVerdict(data.choices?.[0]?.message?.content ?? "", candidates.length);
    return verdict && "chercher" in verdict && !canSearch ? null : verdict;
  } finally {
    clearTimeout(timeout);
    signal?.removeEventListener("abort", onAbort);
  }
}

/** Les candidats pour une orthographe : la personne de ce nom si l'annuaire
 *  la connaît, puis les noms proches. */
async function candidatesFor(name: string, log: DirectoryLog): Promise<DirectoryPerson[]> {
  const step = log(`« ${name} »`);
  const exact = await lookupSender(name);
  const near = (await suggestPeople(name)).map((c) => c.person);
  const people = exact ? [exact.person, ...near.filter((p) => p.email !== exact.person.email)] : near;
  step(people.length > 0 ? people.map((p) => describe(p)).join(" ; ") : "aucune personne proche", people.length === 0);
  return people;
}

/** Le nom écrit, remplacé partout dans le bloc (en-tête YAML ou JSON). */
function renameIn(body: string, from: string, to: string): string {
  return body.split(from).join(to);
}

/** Vérifie les signataires des fichiers du message. Ne lève jamais (sauf
 *  annulation) : en cas de panne, le message part tel quel, et l'aperçu
 *  signale la personne introuvable. */
export async function checkSigners(
  content: string,
  question: string,
  cfg: Config,
  signal?: AbortSignal,
  log: DirectoryLog = () => () => {},
): Promise<SignerReport> {
  let out = content;
  const summaries: string[] = [];
  const done = new Set<string>();
  for (const block of extractFileBlocks(content)) {
    if (!block.complete) continue;
    const name = signerOf(block);
    if (!name || done.has(name)) continue;
    done.add(name);
    try {
      const lookedUp = log(`« ${name} »`);
      const found = await lookupSender(name);
      if (found) {
        lookedUp(describe({ ...found.person, accreds: [found.accred] }));
        continue;
      }
      lookedUp("aucune personne de ce nom exact", true);
      // Noms proches, puis les orthographes que le modèle propose (au plus
      // EXTRA_ROUNDS fois), jusqu'à un choix sûr ou une question.
      const tried = [name];
      let candidates: DirectoryPerson[] = (await suggestPeople(name)).map((c) => c.person);
      const near = log(`noms proches de « ${name} »`);
      near(candidates.length > 0 ? candidates.map((p) => describe(p)).join(" ; ") : "aucun", candidates.length === 0);
      let verdict: Verdict | null = null;
      for (let round = 0; round <= EXTRA_ROUNDS; round++) {
        const decided = log(tr({ fr: "choix du modèle", en: "model's choice" }));
        verdict = await askModel(question, name, tried, candidates, round < EXTRA_ROUNDS, cfg, signal);
        if (!verdict || !("chercher" in verdict)) {
          decided(
            verdict && "choix" in verdict
              ? candidates[verdict.choix - 1].name
              : tr({ fr: "pas de choix sûr : question posée", en: "no safe choice: question asked" }),
            !(verdict && "choix" in verdict),
          );
          break;
        }
        const spellings = verdict.chercher.filter((n) => !tried.some((t) => nameKey(t) === nameKey(n)));
        decided(
          spellings.length > 0
            ? tr({ fr: `autres orthographes à chercher : ${spellings.join(", ")}`, en: `other spellings to search: ${spellings.join(", ")}` })
            : tr({ fr: "orthographes déjà cherchées", en: "spellings already searched" }),
        );
        if (spellings.length === 0) {
          verdict = null;
          break;
        }
        tried.push(...spellings);
        for (const spelling of spellings) {
          for (const p of await candidatesFor(spelling, log)) {
            if (!candidates.some((c) => c.email === p.email)) candidates.push(p);
          }
        }
        candidates = candidates.slice(0, 8);
      }
      if (verdict && "choix" in verdict) {
        const person = candidates[verdict.choix - 1];
        if (block.body.includes(name)) {
          out = out.replace(block.body, renameIn(block.body, name, person.name));
          out += `\n\n_Annuaire EPFL : « ${name} » n'y figure pas sous ce nom ; le document est établi au nom de ${describe(person)}._`;
          summaries.push(`« ${name} » → ${person.name}`);
        } else {
          // Le nom vient d'une version précédente : on ne la réécrit pas, on demande.
          out += `\n\n« ${name} » ne figure pas sous ce nom dans l'annuaire EPFL : vouliez-vous dire ${describe(person)} ?`;
          summaries.push(`« ${name} » : ${person.name} proposé à l'usager`);
        }
      } else {
        const ask =
          verdict && "question" in verdict
            ? verdict.question
            : `« ${name} » est introuvable dans l'annuaire EPFL. Pouvez-vous me donner son nom complet, tel qu'il figure sur people.epfl.ch ?`;
        out += `\n\n${ask}`;
        summaries.push(`« ${name} » introuvable, question à l'usager`);
      }
    } catch (e) {
      if (signal?.aborted) throw e;
      console.warn("[signerCheck] annuaire indisponible, réponse laissée telle quelle :", e);
    }
  }
  return { content: out, summary: summaries.length > 0 ? `Annuaire EPFL : ${summaries.join(" ; ")}` : null };
}
